<template>
  <v-dialog
    :model-value="modelValue"
    max-width="600"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6 text-wrap">
        {{ $t("waste.weigh.title", { name: container ? container.name : "" }) }}
      </v-card-title>
      <v-card-subtitle class="text-wrap waste-subtitle">
        {{ $t("waste.weigh.subtitle") }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="alert"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ alert }}
        </v-alert>
        <container-weighing-fields
          v-model="form"
          :container="container"
          :errors="errors"
          :server-errors="serverErrors"
          :disabled="saving"
          :now="now"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="close"
        >
          {{ $t("waste.form.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="save"
        >
          {{ $t("waste.weigh.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// "Pesa" on the container page (Stage 6 slice 3, L6): any role, also an inactive container. The
// Stage 1 weighing rules, the tare filled in from the container. The latest weighing of the
// current filling becomes the current weight; the recorded litres are never converted.
import { addWasteWeighing, newCommandId } from "@/api/compliance";
import {
  buildWeighingBody,
  weighingErrors,
} from "@/components/Acceptance/weighingForm.mjs";
import ContainerWeighingFields from "@/components/Waste/containerWeighingFields.vue";
import {
  containerWeighingForm,
  serverErrorField,
} from "@/components/Waste/sendOutForm.mjs";

export default {
  name: "WasteWeighContainerDialog",
  components: { ContainerWeighingFields },
  props: {
    modelValue: { type: Boolean, default: false },
    container: { type: Object, default: null },
    revision: { type: Number, default: 0 },
  },
  emits: ["update:modelValue", "saved", "conflict"],
  data: () => ({
    form: containerWeighingForm(null),
    now: new Date(),
    serverErrors: {},
    alert: "",
    saving: false,
    pendingCommandId: "",
  }),
  computed: {
    errors() {
      // The ticket number is optional for a container (a platform scale prints none).
      return weighingErrors(this.form, this.now, { ticketRequired: false });
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        this.stopClock();
        if (!open) return;
        this.now = new Date();
        this.form = containerWeighingForm(this.container, this.now);
        this.serverErrors = {};
        this.alert = "";
        this.pendingCommandId = newCommandId();
        // Keeps the "not in the future" check current while the dialog stays open.
        this.clock = setInterval(() => {
          this.now = new Date();
        }, 30000);
      },
    },
    form() {
      this.serverErrors = {};
    },
  },
  created() {
    this.clock = null;
  },
  beforeUnmount() {
    this.stopClock();
  },
  methods: {
    stopClock() {
      if (this.clock) clearInterval(this.clock);
      this.clock = null;
    },
    close() {
      this.$emit("update:modelValue", false);
    },
    async save() {
      this.now = new Date();
      if (!this.valid || this.saving) return;
      this.saving = true;
      this.alert = "";
      try {
        const detail = await addWasteWeighing(
          this.container.id,
          this.revision,
          this.pendingCommandId,
          buildWeighingBody(this.form),
        );
        this.$emit("saved", detail);
        this.close();
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        if (data.code === "revision_conflict") {
          this.alert = this.$t("waste.weigh.conflict");
          this.$emit("conflict");
        } else {
          const field = serverErrorField(data);
          const key =
            field && field.startsWith("weighing.") ? field.slice(9) : field;
          if (key && key in this.form) {
            this.serverErrors = {
              [key]: data.errorMsg || this.$t("waste.load_error"),
            };
          } else {
            this.alert =
              data.errorMsg ||
              (err && err.message) ||
              this.$t("waste.load_error");
          }
        }
        // A refused save is a new attempt next time; after a network error the same commandId is
        // kept, so a retry that already reached the API is answered as a replay.
        if (err && err.response) this.pendingCommandId = newCommandId();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.waste-subtitle {
  white-space: normal;
}
</style>
